import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useUpdateExpenseCategory } from '@/hooks/useAccounting'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import type { ExpenseCategory } from '@/types/entities'

const PRESET_COLORS = [
  '#3b82f6', // Blue
  '#ef4444', // Red
  '#22c55e', // Green
  '#f97316', // Orange
  '#8b5cf6', // Purple
  '#ec4899', // Pink
  '#06b6d4', // Cyan
  '#eab308', // Yellow
  '#6b7280', // Gray
  '#14b8a6', // Teal
]

interface EditCategoryDialogProps {
  restaurantId: string
  category: ExpenseCategory
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
}

export function EditCategoryDialog({
  restaurantId,
  category,
  open,
  onOpenChange,
  onSuccess,
}: EditCategoryDialogProps) {
  const { t } = useTranslation()
  const updateCategoryMutation = useUpdateExpenseCategory(restaurantId)

  const isSubmittingRef = useRef(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const [formData, setFormData] = useState({
    nameAr: category.nameAr,
    nameFr: category.nameFr,
    icon: category.icon,
    color: category.color,
    isActive: category.isActive,
  })

  useEffect(() => {
    setFormData({
      nameAr: category.nameAr,
      nameFr: category.nameFr,
      icon: category.icon,
      color: category.color,
      isActive: category.isActive,
    })
    setErrors({})
  }, [category])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (isSubmittingRef.current) return

    isSubmittingRef.current = true
    setIsSubmitting(true)
    setErrors({})

    const newErrors: Record<string, string> = {}
    if (!formData.nameAr && !formData.nameFr) {
      newErrors['nameAr'] = t('validation.required')
      newErrors['nameFr'] = t('validation.required')
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      isSubmittingRef.current = false
      setIsSubmitting(false)
      return
    }

    try {
      await updateCategoryMutation.mutateAsync({
        categoryId: category.id,
        nameAr: formData.nameAr,
        nameFr: formData.nameFr,
        icon: formData.icon,
        color: formData.color,
        isActive: formData.isActive,
      })
      onOpenChange(false)
      onSuccess?.()
    } catch (error) {
      setErrors({ form: error instanceof Error ? error.message : t('error.generic') })
    } finally {
      isSubmittingRef.current = false
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-[95vw] sm:w-[90vw] sm:max-w-[90vw] md:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{t('accounting.editCategory')}</DialogTitle>
            <DialogDescription>{t('accounting.editCategoryDescription')}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Name AR */}
            <div className="space-y-2">
              <Label htmlFor="nameAr">{t('accounting.categoryName')} (AR) *</Label>
              <Input
                id="nameAr"
                value={formData.nameAr}
                onChange={(e) => setFormData({ ...formData, nameAr: e.target.value })}
                className={errors['nameAr'] ? 'border-destructive' : ''}
                dir="rtl"
              />
              {errors['nameAr'] && (
                <p className="text-xs text-destructive">{errors['nameAr']}</p>
              )}
            </div>

            {/* Name FR */}
            <div className="space-y-2">
              <Label htmlFor="nameFr">{t('accounting.categoryName')} (FR) *</Label>
              <Input
                id="nameFr"
                value={formData.nameFr}
                onChange={(e) => setFormData({ ...formData, nameFr: e.target.value })}
                className={errors['nameFr'] ? 'border-destructive' : ''}
              />
              {errors['nameFr'] && (
                <p className="text-xs text-destructive">{errors['nameFr']}</p>
              )}
            </div>

            {/* Color Picker */}
            <div className="space-y-2">
              <Label>{t('accounting.color')}</Label>
              <div className="flex flex-wrap gap-2">
                {PRESET_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={`w-8 h-8 rounded-full transition-all ${
                      formData.color === color
                        ? 'ring-2 ring-offset-2 ring-primary scale-110'
                        : 'hover:scale-105'
                    }`}
                    style={{ backgroundColor: color }}
                    onClick={() => setFormData({ ...formData, color })}
                  />
                ))}
              </div>
              <div className="flex items-center gap-2 mt-2">
                <Label htmlFor="customColor" className="text-sm">{t('accounting.customColor')}:</Label>
                <Input
                  id="customColor"
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="w-12 h-8 p-0 border-0 cursor-pointer"
                />
                <span className="text-xs text-muted-foreground">{formData.color}</span>
              </div>
            </div>

            {/* Active Status */}
            <div className="flex items-center justify-between py-2">
              <Label htmlFor="isActive">{t('accounting.activeCategory')}</Label>
              <Switch
                id="isActive"
                checked={formData.isActive}
                onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
              />
            </div>

            {/* Preview */}
            <div className="p-4 rounded-lg border bg-muted/50">
              <p className="text-sm text-muted-foreground mb-2">{t('accounting.preview')}</p>
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-full"
                  style={{ backgroundColor: formData.color }}
                />
                <div>
                  <p className="font-medium">{formData.nameFr || t('accounting.categoryName')}</p>
                  <p className="text-sm text-muted-foreground" dir="rtl">
                    {formData.nameAr || t('accounting.categoryName')}
                  </p>
                </div>
              </div>
            </div>

            {errors['form'] && (
              <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md">
                {errors['form']}
              </div>
            )}
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? t('common.loading') : t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
